import { createContext, use, useEffect, useState } from 'react'
import {
  readLocalPreference,
  writeLocalPreference,
} from '@/lib/local-preference'

type Theme = 'dark' | 'light' | 'system'
type ResolvedTheme = 'dark' | 'light'

interface ThemeProviderProps {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

interface ThemeProviderState {
  theme: Theme
  /**
   * The theme actually in effect: `theme` with 'system' resolved against the OS
   * preference. Consumers that need a concrete light/dark value (Monaco, widget
   * SDKs, icons) must read this rather than running their own matchMedia query,
   * so they can never disagree with the `.dark` class on <html>.
   */
  resolvedTheme: ResolvedTheme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: 'system',
  resolvedTheme: 'light',
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(defaultTheme)
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>('light')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    // Guarded: every route mounts under this provider, above the app's error
    // boundary, so a storage-blocked browser's throw here would take down the
    // whole page.
    const stored = readLocalPreference<Theme | null>(
      storageKey,
      (raw) =>
        ['dark', 'light', 'system'].includes(raw) ? (raw as Theme) : null,
      null,
    )
    if (stored) {
      setTheme(stored)
    }
  }, [storageKey])

  // Track the OS preference continuously. Without this, an automatic
  // light→dark transition (macOS "Auto" at sunset) never reached React: the
  // class-applying effect below only re-ran when `theme` changed, so the site
  // kept the appearance it was loaded with while consumers running their own
  // matchMedia listeners flipped underneath it.
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const update = () => setSystemTheme(mediaQuery.matches ? 'dark' : 'light')

    update()
    mediaQuery.addEventListener('change', update)
    return () => mediaQuery.removeEventListener('change', update)
  }, [])

  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme

  useEffect(() => {
    if (!mounted) return

    const root = window.document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(resolvedTheme)
  }, [resolvedTheme, mounted])

  const value = {
    theme,
    resolvedTheme,
    setTheme: (theme: Theme) => {
      writeLocalPreference(storageKey, theme)
      setTheme(theme)
    },
  }

  return (
    <ThemeProviderContext {...props} value={value}>
      {children}
    </ThemeProviderContext>
  )
}

export function useTheme() {
  const context = use(ThemeProviderContext)

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider')

  return context
}
